import { InlineAddressField } from '@shared/components/inline-address-field/InlineAddressField';
import { SearchInputOption } from '@shared/components/search-input/SearchInput';
import { useFlowsViewContext } from '@shared/contexts/FlowsViewContext';
import {
  PlaceSuggestionResults,
  usePlacesSuggestions,
} from '@shared/hooks/usePlacesSuggestions';
import {
  SAVED_PLACE_PREFIX,
  useSavedPlacesSuggestions,
} from '@shared/hooks/useSavedPlacesSuggestions';
import { formatAddressComponents } from 'clerk_common/google/address';
import { Address } from 'clerk_common/types/address';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { HiOutlineLocationMarker } from 'react-icons/hi';

// TODO(jacob): Remove once react-hook-form setValue types are compatible after upgrade.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReactHookFormBypassType = any;

const mapSuggestions = (
  suggestions: PlaceSuggestionResults
): SearchInputOption[] => {
  return [...suggestions].map((s) => ({
    label: s.structured_formatting.main_text,
    id: s.place_id,
    details: s.structured_formatting.secondary_text,
    icon: <HiOutlineLocationMarker />,
  }));
};
interface AddressSearchProps<T> {
  name: Path<T>;
  required: boolean;
  save: (p: Path<T> | Path<T>[]) => void;
  validateAddress: (address: Address) => boolean;
  allowCustomAddress?: boolean;
}

export const AddressSearch = <T extends FieldValues>(
  p: AddressSearchProps<T>
) => {
  const { setValue: setFormValue, getValues } = useFormContext<T>();
  const { originatorId, organizationId } = useFlowsViewContext();

  const addressPath = `${p.name}.address` as Path<T>;
  const namePath = `${p.name}.name` as Path<T>;
  const locationCodePath = `${p.name}.locationCode` as Path<T>;
  const nameValue = getValues(namePath);

  const { results, setSearchTerm, geocodeByPlaceId, loading, onSelectionMade } =
    usePlacesSuggestions();

  const {
    options: savedPlaceOptions,
    places: savedPlaces,
    setSearchTerm: setSavedSearchTerm,
    loading: savedLoading,
  } = useSavedPlacesSuggestions({ organizationId, originatorId });

  const googleSuggestions = mapSuggestions(results);
  const mergedOptions = [...savedPlaceOptions, ...googleSuggestions];

  const handleSelect = async (id: string) => {
    if (id.startsWith(SAVED_PLACE_PREFIX)) {
      const placeId = id.slice(SAVED_PLACE_PREFIX.length);
      const place = savedPlaces.find((sp) => sp.id === placeId);
      if (!place?.address) return;

      onSelectionMade();

      const address: Address = {
        addressOne: place.address.addressOne ?? '',
        addressTwo: place.address.addressTwo ?? '',
        city: place.address.city ?? '',
        state: place.address.state ?? '',
        zip: place.address.zip ?? '',
        country: place.address.country ?? '',
      };

      setFormValue(addressPath, address as ReactHookFormBypassType);

      if (place.locationCode) {
        setFormValue(
          locationCodePath,
          place.locationCode as ReactHookFormBypassType
        );
      }

      const pathsToSave: Path<T>[] = [addressPath];
      if (nameValue) {
        setFormValue(namePath, '' as ReactHookFormBypassType);
        pathsToSave.push(namePath);
      }
      if (place.locationCode) {
        pathsToSave.push(locationCodePath);
      }
      p.save(pathsToSave.length === 1 ? pathsToSave[0] : pathsToSave);
      return;
    }

    const res = await geocodeByPlaceId(id);
    if (res) {
      const geocoded = res[0];
      if (!geocoded?.address_components?.length) return;

      onSelectionMade();

      const address = formatAddressComponents(geocoded.address_components);

      setFormValue(addressPath, address as ReactHookFormBypassType);
      setFormValue(locationCodePath, '' as ReactHookFormBypassType);

      // NOTE(jacob): We don't currently need the ability to set the
      // location name from the form; we just want to display it if it
      // exists. If the user selects an address and there was a name in
      // the template, we clear it.
      const pathsToSave: Path<T>[] = [addressPath, locationCodePath];
      if (nameValue) {
        setFormValue(namePath, '' as ReactHookFormBypassType);
        pathsToSave.push(namePath);
      }
      p.save(pathsToSave);
    }
  };

  return (
    <InlineAddressField<T>
      save={p.save}
      name={p.name}
      required={p.required}
      validateAddress={p.validateAddress}
      handleSearch={(query) => {
        setSearchTerm(query ?? '');
        setSavedSearchTerm(query ?? '');
      }}
      handleSelect={handleSelect}
      options={mergedOptions}
      loadingSearchResults={loading || savedLoading}
      placeholder="Enter city or zipcode"
      allowCustomAddress={p.allowCustomAddress}
    />
  );
};
